// client/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box } from '@mui/material';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import CartPage from './pages/CartPage';
import ProductDetailPage from './pages/ProductDetailPage';
import SellerProfilePage from './pages/SellerProfilePage';
import AuctionPage from './pages/AuctionPage';
import AuctionDetailPage from './pages/AuctionDetailPage';
import OrderDetailPage from './pages/OrderDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import InvoicePage from './pages/InvoicePage';
import BecomeSeller from './pages/BecomeSeller';
import PaymentMockPage from './pages/PaymentMockPage';
import ReturnsPolicyPage from './pages/ReturnsPolicyPage';
import ComparePage from './pages/ComparePage';
import NotFoundPage from './pages/NotFoundPage';
import FeatureGate from './components/FeatureGate';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './pages/admin/AdminLayout';
import AdminStoreList from './pages/admin/AdminStoreList';
import AdminUserList from './pages/AdminUserList';
import AdminProductList from './pages/AdminProductList';
import AdminAuctionList from './pages/AdminAuctionList';
import AdminOrderList from './pages/AdminOrderList';
import AdminSellerApplications from './pages/AdminSellerApplications';
import AdminCouponList from './pages/AdminCouponList';
import AdminFeatureFlags from './pages/AdminFeatureFlags';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminReturnRequests from './pages/AdminReturnRequests';
import AdminCategoryList from './pages/admin/AdminCategoryList';
import AdminBannerList from './pages/admin/AdminBannerList';
import AdminDocumentList from './pages/admin/AdminDocumentList';
import AdminDocumentForm from './pages/admin/AdminDocumentForm';
import DocumentPrintPage from './pages/DocumentPrintPage';
import PromoStrip from './components/PromoStrip';
import { AuthProvider } from './context/AuthContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { NotificationProvider } from './context/NotificationContext';
import { ActingStoreProvider } from './context/ActingStoreContext';
import { CategoryProvider } from './context/CategoryContext';

function App() {
  return (
    <FeatureFlagProvider>
      <CategoryProvider>
        <AuthProvider>
          <ActingStoreProvider>
            <NotificationProvider>
              <Router>
                <Box className="App" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <PromoStrip />
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1 }}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/compare" element={<ComparePage />} />
                      <Route path="/products/:id" element={<ProductDetailPage />} />
                      <Route path="/sellers/:storeName" element={<SellerProfilePage />} />
                      <Route path="/auctions" element={<FeatureGate flagKey="auctions"><AuctionPage /></FeatureGate>} />
                      <Route path="/auctions/:id" element={<FeatureGate flagKey="auctions"><AuctionDetailPage /></FeatureGate>} />
                      <Route path="/orders/:id" element={<OrderDetailPage />} />
                      <Route path="/orders/:id/invoice" element={<InvoicePage />} />
                      <Route path="/become-seller" element={<FeatureGate flagKey="become_seller"><BecomeSeller /></FeatureGate>} />
                      <Route path="/payment/mock/:payRequestId" element={<PaymentMockPage />} />
                      <Route path="/returns" element={<ReturnsPolicyPage />} />
                      {/* Outside AdminLayout so printing gives just the document. */}
                      <Route path="/documents/:id" element={<ProtectedRoute roles={['admin', 'super_admin']}><DocumentPrintPage /></ProtectedRoute>} />

                      <Route path="/admin" element={<ProtectedRoute roles={['admin', 'super_admin']}><AdminLayout /></ProtectedRoute>}>
                        <Route index element={<AdminAnalytics />} />
                        <Route path="products" element={<AdminProductList />} />
                        <Route path="orders" element={<AdminOrderList />} />
                        <Route path="auctions" element={<AdminAuctionList />} />
                        <Route path="users" element={<AdminUserList />} />
                        <Route path="coupons" element={<AdminCouponList />} />
                        <Route path="invoices" element={<AdminDocumentList key="invoice" type="invoice" />} />
                        <Route path="quotes" element={<AdminDocumentList key="quote" type="quote" />} />
                        <Route path="proformas" element={<AdminDocumentList key="proforma" type="proforma" />} />
                        <Route path="documents/new/:type" element={<AdminDocumentForm />} />
                        <Route path="documents/:id/edit" element={<AdminDocumentForm />} />
                        <Route path="categories" element={<AdminCategoryList />} />
                        <Route path="banners" element={<AdminBannerList />} />
                        <Route path="seller-applications" element={<AdminSellerApplications />} />
                        <Route path="return-requests" element={<AdminReturnRequests />} />
                        <Route path="feature-flags" element={<AdminFeatureFlags />} />
                        <Route
                          path="stores"
                          element={<ProtectedRoute roles={['super_admin']}><AdminStoreList /></ProtectedRoute>}
                        />
                      </Route>

                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Box>
                  <Footer />
                </Box>
              </Router>
            </NotificationProvider>
          </ActingStoreProvider>
        </AuthProvider>
      </CategoryProvider>
    </FeatureFlagProvider>
  );
}

export default App;
